/* eslint-disable react/no-deprecated */
import React, { useState, useRef, useEffect } from 'react';
import { string, number } from 'prop-types';
import classNames from 'classnames/bind';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { isMobileWidth } from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import ArrowRight from '../../assets/svg/icons-navigation-chevron-right.svg';
import * as styles from './Carousel.css';

import carouselTheme from './carouselTheme';

const cx = classNames.bind(styles);

const holdAndSwipeConstants = {
    dragSpeed: 1.25,
    itemSideOffsets: 12,
};
const dynamicRelVisNavCarouselCenter = 'dynamic-rel-visNav-carousel-center';

const SliderItemContent = ({
    children,
    listParentRef,
    automationId,
    automationItemId,
    theme,
    sliderItemContentStyle,
    sliderItemContainerStyle,
}) => {
    const sliderGridStyle = dt([
        'flex',
        'flex-nowrap',
        'transition-transform',
        'duration-[.4s]',
        'ease-in-out',
        'my-0',
        'smMd:mb-1.5',
    ]);

    return (
        <div
            className={`${sliderGridStyle} ${cx('carouselSliderItemBlock')}  ${
                children.length < 6 && theme === dynamicRelVisNavCarouselCenter
                    ? dt(['md:justify-center', 'md:gap-x-10'])
                    : carouselTheme[theme]?.carouselDivContainer
            } ${sliderItemContainerStyle}`}
            ref={listParentRef}
            data-automation-id={automationId}
            style={{ transform: 'translate(0px)' }}
        >
            {children.map((item, index) => {
                return (
                    <div
                        key={`grid${index + 1}`}
                        data-automation-id={automationItemId}
                        className={`${
                            carouselTheme[theme]?.carouselItem
                        } ${sliderItemContentStyle} ${cx('carouselSliderItem')}`}
                    >
                        {item}
                    </div>
                );
            })}
        </div>
    );
};

const Carousel = (props) => {
    const prevScrollY = useRef(0);
    let listParentRef = useRef(null);
    let wrapperRef = useRef(null);
    const [disableLeftArrow, setdisableLeftArrow] = useState(true);
    const [disableRightArrow, setdisableRightArrow] = useState(false);
    const [goingUp, setGoingUp] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [startX, setStartX] = useState(null);
    const [isDown, setIsDown] = useState(false);
    const [transLeftOffset, setTranslateLeftOffset] = useState(0);

    /* istanbul ignore next */
    const handleScroll = () => {
        const currentScrollY = window.scrollY;
        const isGoingUp = prevScrollY.current > currentScrollY && !goingUp;
        setGoingUp(isGoingUp);
        prevScrollY.current = currentScrollY;
    };

    const getDimensions = () => {
        if (!wrapperRef || !listParentRef) return {};
        const { width: wrapperWidth } = wrapperRef.getBoundingClientRect() || {};
        const { scrollWidth: parentWidth, childElementCount } = listParentRef;
        return {
            wrapperWidth,
            parentWidth,
            childElementCount,
        };
    };

    const getMaxProductsWithoutArrows = () => {
        const { theme } = props;
        if (theme === 'inslider-carousel') return 3;
        if (theme === 'hotdeals-carousel') return 6;
        if (theme === 'rrzones-carousel') return 7;
        if (theme === 'cart-carousel') return 5;
        return 4;
    };

    const disableArrowCheck = (spostion) => {
        const maxProducts = getMaxProductsWithoutArrows();
        const { wrapperWidth = 0, parentWidth = 0, childElementCount = 0 } = getDimensions() || {};
        const maxMoveToLeft = 0;
        const maxMoveToRight = wrapperWidth - parentWidth;
        const rightArrowCheck = spostion <= maxMoveToRight || childElementCount <= maxProducts;
        const leftArrowCheck = spostion >= maxMoveToLeft || childElementCount <= maxProducts;
        setdisableRightArrow(rightArrowCheck);
        setdisableLeftArrow(leftArrowCheck);
    };

    const onResizeCallback = () => {
        disableArrowCheck(scrollPosition);
    };

    /* istanbul ignore next */
    useEffect(() => {
        if (isMobileWidth()) return;
        disableArrowCheck(scrollPosition);
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', onResizeCallback(window));

        return () => {
            if (!__SERVER__) {
                window.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', onResizeCallback(window));
            }
        };
    }, []);

    const getListParentRef = (ref) => {
        listParentRef = ref || {};
    };

    /* istanbul ignore next */
    const getNewScrollPosition = (wrapperWidth, parentWidth, childElementCount) => {
        const childWidth = parentWidth / childElementCount;
        let childCountWidth = 0;
        for (let i = 1; i <= childElementCount; i++) {
            childCountWidth = i * childWidth;
            if (childCountWidth > wrapperWidth) {
                break;
            } else childCountWidth = 0;
        }
        return childCountWidth > 0 ? childCountWidth - childWidth : wrapperWidth;
    };

    /* istanbul ignore next */
    const scrollCartridge = (scrollToRight, isScrollEvt = false) => () => {
        const { wrapperWidth = 0, parentWidth = 0, childElementCount = 0 } = getDimensions() || {};
        const maxMoveToLeft = 0;
        const maxMoveToRight = wrapperWidth - parentWidth;
        if (scrollToRight) {
            const newScrollPosition =
                scrollPosition - getNewScrollPosition(wrapperWidth, parentWidth, childElementCount);
            const spos =
                newScrollPosition >= maxMoveToRight
                    ? newScrollPosition
                    : props.theme === 'viz-nav-carousel'
                    ? maxMoveToRight - 40
                    : maxMoveToRight - 7;
            setScrollPosition(spos);
            if (listParentRef && listParentRef.style) {
                listParentRef.style.transform = `translate(${spos}px)`;
            }

            if (!isScrollEvt) {
                disableArrowCheck(spos);
            }
        } else {
            const newScrollPosition =
                scrollPosition + getNewScrollPosition(wrapperWidth, parentWidth, childElementCount);
            const spos = newScrollPosition <= maxMoveToLeft ? newScrollPosition : maxMoveToLeft;
            setScrollPosition(spos);
            if (listParentRef) listParentRef.style.transform = `translate(${spos}px)`;
            disableArrowCheck(spos);
        }
    };

    const renderNavigationButtons = () => {
        const leftArrowClass = cx({ iconClass: true, disabledArrow: disableLeftArrow });
        const rightArrowClass = cx({ iconClass: true, disabledArrow: disableRightArrow });
        /* istanbul ignore next */
        if (!listParentRef && isMobileWidth() && disableLeftArrow && disableRightArrow) return null;
        const { customLeftButtonSliderClass, customRightButtonSliderClass } = props;
        const btnPositionStyle = props.carouselImageHeight
            ? { margin: '0px', top: `${props.carouselImageHeight / 2 - 20}px` }
            : null;
        return (
            <>
                <button
                    type="button"
                    className={cx('arrow', 'leftArrow', customLeftButtonSliderClass)}
                    disabled={disableLeftArrow}
                    onClick={scrollCartridge(false)}
                    data-automation-id="carousel-leftArrow"
                    style={btnPositionStyle}
                >
                    <Icon
                        iconType="svg"
                        className="arrow-icon"
                        width="24px"
                        height="24px"
                        viewBox="0 0 20 20"
                        pathClassName={leftArrowClass}
                    >
                        <ArrowRight />
                    </Icon>
                </button>
                <button
                    type="button"
                    className={cx('arrow', 'rightArrow', customRightButtonSliderClass)}
                    disabled={disableRightArrow}
                    onClick={scrollCartridge(true)}
                    data-automation-id="carousel-rightArrow"
                    style={btnPositionStyle}
                >
                    <Icon
                        iconType="svg"
                        className="arrow-icon"
                        width="24px"
                        height="24px"
                        viewBox="0 0 24 24"
                        pathClassName={rightArrowClass}
                    >
                        <ArrowRight />
                    </Icon>
                </button>
            </>
        );
    };

    const giveIntValOf = (el) => {
        // extracting 20 from translate(20px) and converting it to integer with parsInt
        return parseInt(el.replace('translate(', '').replace('px)', ''), 10);
    };

    const handleMouseDown = (e) => {
        e.persist();
        const { parentWidth = 0 } = getDimensions() || {};
        const _startX = e.pageX - parentWidth;
        const _transLeftOffset =
            listParentRef?.style && giveIntValOf(listParentRef.style.transform);
        setStartX(_startX);
        setIsDown(true);
        setTranslateLeftOffset(_transLeftOffset);
    };

    const handleMouseLeave = () => {
        setIsDown(false);
    };

    const handleMouseUp = () => {
        handleSnap();
    };

    const handleMouseMove = (e) => {
        const { wrapperWidth = 0, parentWidth = 0, childElementCount = 0 } = getDimensions() || {};

        if (!isDown) return;
        e.preventDefault();

        const x = e.pageX - parentWidth;
        const walk = (x - startX) * holdAndSwipeConstants.dragSpeed;
        const distanceWalked = transLeftOffset + walk;
        const itemWidth = parentWidth / childElementCount;
        const stopMovePosition = wrapperWidth - childElementCount * itemWidth - 50;

        if (distanceWalked >= 50 || distanceWalked <= stopMovePosition) return;

        setScrollPosition(distanceWalked);
        disableArrowCheck(distanceWalked);

        if (listParentRef)
            listParentRef.style.cssText = `
                transform: translate(${distanceWalked}px);
                transition: transform 0.5s cubic-bezier(.25,.72,.51,.96);
        `;
    };

    // handle Snap To Sides
    const handleSnap = () => {
        const { wrapperWidth = 0, parentWidth = 0, childElementCount = 0 } = getDimensions() || {};
        setIsDown(false);
        if (listParentRef?.style) {
            const tempThresholdOffset = giveIntValOf(listParentRef.style.transform);
            const itemWidth = parentWidth / childElementCount;
            const end = wrapperWidth - childElementCount * itemWidth;

            if (tempThresholdOffset > 0 || (tempThresholdOffset && tempThresholdOffset < end)) {
                setIsDown(false);
                if (listParentRef)
                    listParentRef.style.cssText = `
                    transform: translate(${tempThresholdOffset > 0 ? 0 : end}px);
                    transition: transform 0.5s cubic-bezier(.25,.72,.51,.96);
            `;
            }
        }
    };

    const { wrapperClass, contentWrapperClass, automationId } = props;
    const extraPropsToChildren = !isMobileWidth()
        ? {
              listParentRef: getListParentRef,
          }
        : {};
    const childrenElement = React.cloneElement(
        <SliderItemContent {...props} />,
        extraPropsToChildren
    );

    return (
        <div
            className={cx(
                'sliderWrapper',
                'carouselWrapperContainer',
                'mobileSlider',
                wrapperClass
            )}
            data-automation-id={automationId}
            onMouseDown={(mouseDownEvent) => {
                handleMouseDown(mouseDownEvent);
            }}
            onMouseLeave={(mouseLeaveEvent) => {
                handleMouseLeave(mouseLeaveEvent);
            }}
            onMouseUp={(mouseUpEvent) => {
                handleMouseUp(mouseUpEvent);
            }}
            onMouseMove={(mouseMoveEvent) => {
                handleMouseMove(mouseMoveEvent);
            }}
        >
            <div
                className={`${cx(
                    'contentWrapper',
                    'carouselScrollContent',
                    contentWrapperClass
                )} ${dt(['lg:overflow-hidden', 'smMd:overflow-auto'])}`}
                style={{
                    width: '100%',
                    scrollbarWidth: 'none',
                }}
                /* istanbul ignore next */
                ref={(el) => {
                    if (el) {
                        wrapperRef = el;
                    }
                }}
            >
                {childrenElement}
            </div>
            {renderNavigationButtons()}
        </div>
    );
};

SliderItemContent.defaultProps = {
    automationId: 'nucleus-carousel-component',
    automationItemId: 'carousel-item',
    theme: 'carousel',
    sliderItemContentStyle: '',
    sliderItemContainerStyle: '',
};

SliderItemContent.propTypes = {
    automationId: string,
    automationItemId: string,
    theme: string,
    sliderItemContentStyle: string,
    sliderItemContainerStyle: string,
};

Carousel.defaultProps = {
    wrapperClass: '',
    contentWrapperClass: '',
    customLeftButtonSliderClass: '',
    customRightButtonSliderClass: '',
    automationId: 'nucleus-carousel-component',
    automationItemId: 'carousel-item',
    theme: 'carousel',
    carouselImageHeight: 0,
};

Carousel.propTypes = {
    wrapperClass: string,
    contentWrapperClass: string,
    customLeftButtonSliderClass: string,
    customRightButtonSliderClass: string,
    automationId: string,
    automationItemId: string,
    theme: string,
    carouselImageHeight: number,
};

export default Carousel;
